// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, { TitleTypographyStyles } from "@awc/App/Components/Common/TitleTypography";
import { CustomFieldDefinition } from "@awc/CustomFieldDefinition";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xParserOptions } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";

export interface CategoryModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        name: string;
        image: string;
    };
}

const enum fields {
    title = "title",
    description = "description",
    categoriesList = "categoriesList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.categoriesList]: CategoryModel[];
    };
}

type Props = OwnProps;

export interface FeaturedCategoriesStyles {
    container?: InjectableCss;
    titleTypographyStyles?: TitleTypographyStyles;
    description?: TypographyPresentationProps;
    categoriesContainer?: InjectableCss;
    categoryContainer?: InjectableCss;
    categoryImage?: LazyImageProps;
    categoryLink?: LinkPresentationProps;
    clickableImage?: ClickablePresentationProps;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;
const FeaturedCategories = ({ fields: { title, description, categoriesList } }: Props) => {
    const categoriesLinks = useGetLinks(categoriesList, o => o.fields.destination);

    return (
        <StyledWrapper as="section" {...styles.container} data-test-selector="FeaturedCategories">
            <TitleTypography extendedStyles={styles.titleTypographyStyles}>{title}</TitleTypography>
            {description && <Typography {...styles.description}>{parse(description, xParserOptions)}</Typography>}
            <StyledWrapper {...styles.categoriesContainer}>
                {categoriesList.map((category, index) => (
                    <StyledWrapper
                        key={category.fields.name}
                        {...styles.categoryContainer}
                        data-test-selector="FeaturedCategories-category-item"
                        as="article"
                    >
                        <Clickable href={categoriesLinks[index].url} {...styles.clickableImage}>
                            <LazyImage
                                src={category.fields.image}
                                altText={category.fields.name}
                                {...styles.categoryImage}
                            />
                        </Clickable>
                        <Link
                            aria-label={category.fields.name}
                            href={categoriesLinks[index].url}
                            target={category.fields.openInNewWindow ? "_blank" : ""}
                            {...styles.categoryLink}
                        >
                            {category.fields.name}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: FeaturedCategories,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Contextual",
            },
            {
                name: fields.description,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.categoriesList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Category name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
